/* eslint-disable react/prop-types */
import React from 'react';
import { HandHeart, Wind } from 'lucide-react';

/**
 * Suggested Mentorship Pairing™ — capability transfer, never competition.
 *
 * Surfaced inside the Counselor Capability page detail panel and in the
 * Weekly Movement Digest. Calm, lightweight; no rankings.
 *
 * Phase 4b additions:
 *  - Depth-debt chip when learner.learner_depth_debt is true. Phrased as
 *    "depth consistency softening — movement faster than depth stabilization".
 *    Never "low quality" or "weak".
 *  - "Co-holding suggested" mode shown first when depth-debt is present:
 *    sometimes the counselor needs shared emotional holding, not skill
 *    transfer.
 */
export default function MentorshipPairingCard({ pairing, labels = {} }) {
  if (!pairing) return null;
  const isCoHolding = pairing.modes?.[0] === 'co-holding';
  return (
    <article
      data-testid={`mentorship-pairing-${pairing.mentor.user_id}-${pairing.learner.user_id}`}
      className="rounded-xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-3 space-y-2"
    >
      <header className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#8C6239]">
          <HandHeart className="w-3 h-3" /> Suggested Mentorship Pairing™
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em] bg-[#F4F2EE] text-[#5C5955] rounded-full px-2 py-0.5">
          {labels[pairing.rung] || pairing.rung_label}
        </span>
      </header>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Side label="Strong-at"      person={pairing.mentor}  tone="emerald" />
        <Side label="Could refine"   person={pairing.learner} tone="amber" />
      </div>

      {pairing.learner_depth_debt && (
        <div data-testid={`depth-debt-chip-${pairing.learner.user_id}`}
              className="flex items-start gap-1.5 text-[10px] text-[#5C3D1F] rounded-lg bg-[#FAF1E1] border border-[#E5D6BE] px-2 py-1.5">
          <Wind className="w-3 h-3 mt-0.5 shrink-0 text-[#8C6239]" />
          <span className="leading-tight">
            {pairing.learner_depth_state || "depth consistency softening"}
          </span>
        </div>
      )}

      <ul className="text-[10px] text-[#5C3D1F] space-y-0.5 pt-1">
        {(pairing.modes || []).map((m, i) => {
          const isFirstCoHolding = i === 0 && m === 'co-holding';
          return (
            <li key={m}
                data-testid={`mode-${m.replace(/[^a-z]/gi, '-')}`}
                className={`flex items-center gap-1.5 ${isFirstCoHolding ? 'text-[#5C3D1F] font-medium' : ''}`}>
              <span className={`w-1 h-1 rounded-full ${isFirstCoHolding ? 'bg-[#8C6239]' : 'bg-[#8C6239] opacity-60'}`} />
              {m}{isFirstCoHolding && <span className="ml-1 opacity-70">· shared emotional holding, not skill transfer</span>}
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] text-[#8A8681] italic">
        {isCoHolding
          ? "Co-holding suggested — capacity stewardship together."
          : "Capability transfer. Not a performance comparison."}
      </p>
    </article>
  );
}

function Side({ label, person, tone }) {
  const ring = tone === 'emerald'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
    : 'border-amber-200 bg-amber-50 text-amber-900';
  return (
    <div className={`rounded-lg border ${ring} px-2 py-1.5`}>
      <p className="text-[9px] uppercase tracking-[0.14em] opacity-70">{label}</p>
      <p className="text-[12px] leading-tight truncate">{person.name}</p>
      <p className="text-[10px] tabular-nums opacity-70">
        {person.velocity_pct}% · {person.sample_count}
      </p>
    </div>
  );
}
